import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Download,
  ChevronLeft,
  Calendar,
  Users,
  DollarSign,
  Activity,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { dashboardApi, getLocalizedValue } from '@/services/clinicApi';

const ClinicReports = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchReportData();
  }, [isAuthenticated, navigate, reportType, selectedPeriod]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await dashboardApi.getCharts();
      if (response?.data?.data) {
        setChartData(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-80" />
            <Skeleton className="h-80" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinic-reports">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {lang === 'ar' ? 'التقارير والتحليلات' : 'Reports & Analytics'}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{lang === 'ar' ? 'هذا الأسبوع' : 'This Week'}</SelectItem>
                  <SelectItem value="month">{lang === 'ar' ? 'هذا الشهر' : 'This Month'}</SelectItem>
                  <SelectItem value="quarter">{lang === 'ar' ? 'هذا الربع' : 'This Quarter'}</SelectItem>
                  <SelectItem value="year">{lang === 'ar' ? 'هذا العام' : 'This Year'}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'تصدير PDF' : 'Export PDF'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Report Types */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${reportType === 'overview' ? 'ring-2 ring-teal-500' : ''}`}
            onClick={() => setReportType('overview')}
          >
            <CardContent className="p-6 text-center">
              <BarChart3 className="h-8 w-8 mx-auto mb-3 text-teal-600" />
              <p className="font-medium">{lang === 'ar' ? 'نظرة عامة' : 'Overview'}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${reportType === 'patients' ? 'ring-2 ring-teal-500' : ''}`}
            onClick={() => setReportType('patients')}
          >
            <CardContent className="p-6 text-center">
              <Users className="h-8 w-8 mx-auto mb-3 text-blue-600" />
              <p className="font-medium">{lang === 'ar' ? 'المرضى' : 'Patients'}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${reportType === 'financial' ? 'ring-2 ring-teal-500' : ''}`}
            onClick={() => setReportType('financial')}
          >
            <CardContent className="p-6 text-center">
              <DollarSign className="h-8 w-8 mx-auto mb-3 text-green-600" />
              <p className="font-medium">{lang === 'ar' ? 'المالية' : 'Financial'}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${reportType === 'appointments' ? 'ring-2 ring-teal-500' : ''}`}
            onClick={() => setReportType('appointments')}
          >
            <CardContent className="p-6 text-center">
              <Calendar className="h-8 w-8 mx-auto mb-3 text-purple-600" />
              <p className="font-medium">{lang === 'ar' ? 'المواعيد' : 'Appointments'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-teal-600" />
                {lang === 'ar' ? 'اتجاه المواعيد' : 'Appointments Trend'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar' ? 'عدد المواعيد على مدار الوقت' : 'Appointment count over time'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-lg">
                {chartData?.appointments_trend ? (
                  <div className="w-full h-full p-4">
                    <div className="flex items-end justify-between h-full gap-2">
                      {chartData.appointments_trend.data?.map((value, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center gap-2">
                          <div 
                            className="w-full bg-teal-500 rounded-t transition-all"
                            style={{ height: `${(value / Math.max(...chartData.appointments_trend.data)) * 150}px` }}
                          />
                          <span className="text-xs text-slate-500">
                            {chartData.appointments_trend.labels?.[index]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500">{lang === 'ar' ? 'لا توجد بيانات' : 'No data available'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-purple-600" />
                {lang === 'ar' ? 'حالة المواعيد' : 'Appointment Status'}
              </CardTitle>
              <CardDescription>
                {lang === 'ar' ? 'توزيع المواعيد حسب الحالة' : 'Distribution by status'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-lg">
                {chartData?.status_breakdown ? (
                  <div className="w-full p-4">
                    {Object.entries(chartData.status_breakdown).map(([status, count], index) => (
                      <div key={status} className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            index === 0 ? 'bg-amber-500' :
                            index === 1 ? 'bg-blue-500' :
                            index === 2 ? 'bg-green-500' :
                            index === 3 ? 'bg-red-500' : 'bg-purple-500'
                          }`} />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{status}</span>
                        </div>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500">{lang === 'ar' ? 'لا توجد بيانات' : 'No data available'}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>{lang === 'ar' ? 'ملخص التقرير' : 'Report Summary'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <Activity className="h-10 w-10 mx-auto mb-3 text-teal-600" />
                <p className="text-3xl font-bold text-slate-900 dark:text-white">--</p>
                <p className="text-sm text-slate-500 mt-1">{lang === 'ar' ? 'إجمالي المواعيد' : 'Total Appointments'}</p>
              </div>
              <div className="text-center p-6 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <Users className="h-10 w-10 mx-auto mb-3 text-blue-600" />
                <p className="text-3xl font-bold text-slate-900 dark:text-white">--</p>
                <p className="text-sm text-slate-500 mt-1">{lang === 'ar' ? 'مرضى جدد' : 'New Patients'}</p>
              </div>
              <div className="text-center p-6 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <DollarSign className="h-10 w-10 mx-auto mb-3 text-green-600" />
                <p className="text-3xl font-bold text-slate-900 dark:text-white">--</p>
                <p className="text-sm text-slate-500 mt-1">{lang === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClinicReports;
